import { useEffect, useState, useContext, useRef } from 'react';

import { formatNominationData } from '../../../../utils/formatNominationData';
import { syncEmailToOrganisation } from '../../../../utils/syncEmailToOrganisation';

import { axiosGet } from '../../../../utils/axiosGet';
import { axiosDelete } from '../../../../utils/axiosDelete';
import { axiosPost } from '../../../../utils/axiosPost';
import { axiosRequest } from '../../../../utils/axiosHttp';
import { CommonCollections } from '../../../../App';
import { getNominationFormData } from '../../../../utils/getNominationFormData';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { useProposerCollections } from '../../../../context/ProposerCollections';
import { useAdminCollections } from '../../../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import { useAdminActionCollections } from '../../../../context/AdminActionCollections';
import { ELECTION_TYPE } from '../../../../utils/constants';
import { devError } from '../../../../utils/devLog';
import { useWizardNotification } from './useWizardNotification';

const useNominationWizard = ({
  setValue,
  watch,
  resetField,
  trigger,
  setHideButtons,
  errors,
}) => {
  const { userDetails } = useLoggedInUserContextProvider();
  const {
    organisations,
    sectionalCommittees,
    usersArray,
    activeFellowsArray,
    titlesTypes,
  } = useStaticCollections();
  const { userDraftNominationArray } = useProposerCollections();
  const {
    nominationArray,
    nominationStateArray,
    candidateArray,
    draftNominationArray,
    refreshNominations,
    setRefreshNominations,
  } = useAdminCollections();

  const { nominationId, setNominationId } = useContext(CommonCollections);
  const { enableSubmissionEdit } = useAdminActionCollections();

  const [strapiCandidateId, setStrapiCandidateId] = useState(null);
  const [nominationStatusId, setNominationStatusId] = useState(0);
  const [originalStatusId, setOriginalStatusId] = useState(0);
  const { notification, showNotification } = useWizardNotification();
  const [submitPending, setSubmitPending] = useState(false);
  const [pendingElectionChange, setPendingElectionChange] = useState(null);
  const isInitialLoad = useRef(true);
  const previousElectionTypeId = useRef(null);
  const pendingLoadedElectionType = useRef(null);

  useEffect(() => {
    if (userDetails && activeFellowsArray) {
      if (!userDetails?.roles?.includes('admin')) {
        const currentUserFellow = activeFellowsArray.find(
          (fellow) => fellow.id === userDetails.fellowId,
        );

        if (currentUserFellow && !watch('proposerFellowId')) {
          setValue('proposerFellowId', {
            value: currentUserFellow.id,
            label: currentUserFellow.attributes.label,
          });
        }
      } else if (watch('proposerFellowId')) {
        setValue('proposerFellowId', null);
      }
    }
  }, [userDetails, activeFellowsArray, setValue, watch]);

  const electionTypeId = parseInt(watch('electionTypeId'));

  const candidateName = watch('name');
  const candidateLastName = watch('lastName');
  const candidateEmail = watch('email');

  useEffect(() => {
    if (!candidateArray) return;

    let match = null;

    if (candidateName && candidateLastName) {
      match = candidateArray.find(
        (item) =>
          item.attributes.name?.toLowerCase() === candidateName.toLowerCase() &&
          item.attributes.lastName?.toLowerCase() ===
            candidateLastName.toLowerCase(),
      );
    }

    if (!match && candidateEmail) {
      match = candidateArray.find(
        (item) =>
          item.attributes.email?.toLowerCase() === candidateEmail.toLowerCase(),
      );
    }

    if (match) {
      setStrapiCandidateId(match.id);
    }
  }, [candidateName, candidateLastName, candidateEmail, candidateArray]);

  useEffect(() => {
    const errorMessages = {
      'expired-nomination':
        'Recently expired nominations are not eligible for submission.',
      'already-elected':
        'Candidate has already been elected and cannot be nominated again.',
      'deceased-candidate': 'Candidate is deceased and cannot be nominated.',
      'active-nomination-duplicate':
        'This candidate already has an active nomination by another proposer.',
      'active-nomination-warning':
        'A candidate with this name may already have an active nomination by another proposer. Please verify the candidate details.',
      'draft-duplicate':
        'Another proposer is already preparing a nomination for this candidate.',
      'draft-duplicate-warning':
        'Another proposer may be preparing a nomination for a candidate with this name. Please verify the candidate details.',
    };

    const errorTypes = [
      errors?.email?.type,
      errors?.name?.type,
      errors?.lastName?.type,
    ];
    const blockingErrorType = errorTypes.find((type) => errorMessages[type]);

    if (blockingErrorType) {
      showNotification('Form Errors', errorMessages[blockingErrorType]);
    }
  }, [
    errors?.email?.type,
    errors?.name?.type,
    errors?.lastName?.type,
    showNotification,
  ]);

  useEffect(() => {
    try {
      (async () => {
        if (nominationId) {
          const draftNominationId =
            typeof nominationId === 'string' && nominationId.includes('d')
              ? parseInt(nominationId.replace(/d/g, ''))
              : nominationId;

          const draftNominations = !userDetails?.roles?.includes('admin')
            ? userDraftNominationArray
            : draftNominationArray;

          let nominationFormValues = '';
          const draftNominationById =
            typeof nominationId === 'string' && nominationId.includes('d')
              ? draftNominations.find((item) => item.id === draftNominationId)
              : null;

          if (draftNominationById) {
            nominationFormValues = JSON.parse(
              draftNominationById.attributes.value,
            );

            Object.keys(nominationFormValues).forEach((item) => {
              setValue(item, nominationFormValues[item]);
            });

            syncEmailToOrganisation(nominationFormValues, watch, setValue);

            trigger();
          } else {
            const nominationById = nominationArray.find(
              (item) => item.id === nominationId,
            );
            if (!nominationById) return;

            nominationFormValues = nominationById?.attributes?.value
              ? JSON.parse(nominationById.attributes.value)
              : null;

            if (!nominationFormValues) {
              nominationFormValues = await getNominationFormData(
                nominationById,
                organisations,
              );
            }

            if (nominationFormValues) {
              Object.keys(nominationFormValues).forEach((item) => {
                setValue(item, nominationFormValues[item]);
              });

              syncEmailToOrganisation(nominationFormValues, watch, setValue);

              trigger();
            }
            const { candidateId, nominationStatusTypeId } =
              nominationById.attributes;
            setStrapiCandidateId(candidateId);

            setOriginalStatusId(nominationStatusTypeId);
          }

          if (
            nominationFormValues &&
            nominationFormValues.electionTypeId != null
          ) {
            pendingLoadedElectionType.current = parseInt(
              nominationFormValues.electionTypeId,
            );
          }
          isInitialLoad.current = false;
        }

        if (!userDetails?.roles?.includes('admin')) {
          setHideButtons(!enableSubmissionEdit);
        }
      })();
    } catch (error) {
      devError('Error setting nomination form values:', error);
    }
  }, [
    nominationId,
    setValue,
    resetField,
    trigger,
    watch,
    organisations,
    nominationArray,
    userDraftNominationArray,
    draftNominationArray,
    userDetails?.roles,
    enableSubmissionEdit,
    setHideButtons,
  ]);

  const getElectionTypeClearableFields = (targetElectionTypeId) => {
    if (
      targetElectionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER ||
      targetElectionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
    ) {
      return [
        { key: 'sectionalCommitteeId', label: 'Sectional Committee' },
        { key: 'disciplineDescriptor', label: 'Discipline Descriptor' },
        { key: 'overlapCommittee', label: 'Overlap Committee' },
        {
          key: 'overlapSectionalCommitteeId',
          label: 'Overlap Sectional Committee',
        },
        {
          key: 'overlapDisciplineDescriptor',
          label: 'Overlap Discipline Descriptor',
        },
        {
          key: 'interdisciplinaryCommittee',
          label: 'Interdisciplinary Committee',
        },
        { key: 'sectionalCommitteeNotes', label: 'Sectional Committee Notes' },
        { key: 'overlapCommitteeNotes', label: 'Overlap Committee Notes' },
        { key: 'refereeFive', label: 'Referee 5' },
        { key: 'refereeFiveFellowId', label: 'Referee 5 Fellow' },
        { key: 'refereeSix', label: 'Referee 6' },
        { key: 'refereeSixFellowId', label: 'Referee 6 Fellow' },
      ];
    }
    if (targetElectionTypeId === ELECTION_TYPE.ORDINARY) {
      return [{ key: 'nationalityId', label: 'Nationality' }];
    }
    return [];
  };

  const hasNonEmptyValue = (value) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value === 'none'
    ) {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  const applyElectionTypeReset = (targetElectionTypeId) => {
    if (
      targetElectionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER ||
      targetElectionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
    ) {
      if (watch('sectionalCommitteeId'))
        setValue('sectionalCommitteeId', 'none');
      if (watch('disciplineDescriptor')) setValue('disciplineDescriptor', []);
      if (watch('overlapCommittee')) setValue('overlapCommittee', 'none');
      if (watch('overlapSectionalCommitteeId')) {
        setValue('overlapSectionalCommitteeId', 'none', {
          shouldValidate: true,
        });
      }
      if (watch('overlapDisciplineDescriptor'))
        setValue('overlapDisciplineDescriptor', []);
      if (watch('interdisciplinaryCommittee'))
        setValue('interdisciplinaryCommittee', []);
      if (watch('sectionalCommitteeNotes'))
        setValue('sectionalCommitteeNotes', '');
      if (watch('overlapCommitteeNotes')) setValue('overlapCommitteeNotes', '');
      if (watch('refereeFive')) setValue('refereeFive', 'none');
      if (watch('refereeFiveFellowId')) setValue('refereeFiveFellowId', 'none');
      if (watch('refereeSix')) setValue('refereeSix', 'none');
      if (watch('refereeSixFellowId')) setValue('refereeSixFellowId', 'none');
    } else if (targetElectionTypeId === ELECTION_TYPE.ORDINARY) {
      if (watch('nationalityId')) setValue('nationalityId', []);
    }
  };

  useEffect(() => {
    if (isInitialLoad.current) {
      previousElectionTypeId.current = electionTypeId;
      return;
    }

    if (pendingLoadedElectionType.current !== null) {
      if (pendingLoadedElectionType.current === electionTypeId) {
        previousElectionTypeId.current = electionTypeId;
        pendingLoadedElectionType.current = null;
      }
      return;
    }

    if (!nominationId) {
      return;
    }

    if (previousElectionTypeId.current === electionTypeId) {
      return;
    }

    if (pendingElectionChange !== null) {
      return;
    }

    const clearableFields = getElectionTypeClearableFields(electionTypeId);
    const fieldsWithValues = clearableFields
      .filter(({ key }) => hasNonEmptyValue(watch(key)))
      .map(({ label }) => label);

    if (fieldsWithValues.length === 0) {
      previousElectionTypeId.current = electionTypeId;
      applyElectionTypeReset(electionTypeId);
      return;
    }

    setPendingElectionChange({
      from: previousElectionTypeId.current,
      to: electionTypeId,
      fields: fieldsWithValues,
    });
  }, [
    electionTypeId,
    nominationId,
    setValue,
    watch,
    trigger,
    pendingElectionChange,
  ]);

  const handleElectionTypeConfirm = () => {
    if (!pendingElectionChange) return;
    applyElectionTypeReset(pendingElectionChange.to);
    previousElectionTypeId.current = pendingElectionChange.to;
    setPendingElectionChange(null);
  };

  const handleElectionTypeCancel = () => {
    if (!pendingElectionChange) return;
    const { from } = pendingElectionChange;
    previousElectionTypeId.current = from;
    setValue('electionTypeId', String(from));
    setPendingElectionChange(null);
  };

  const onSubmit = async (data) => {
    data.originalStatusId = originalStatusId;
    data.strapiRowId = strapiCandidateId;
    data.userId = userDetails.id;
    data.userEmail = userDetails.email;

    const formattedData = await formatNominationData(
      data,
      sectionalCommittees,
      titlesTypes,
    );

    setSubmitPending(true);
    try {
      const response = await axiosRequest('post', 'nomination-post', {
        data: formattedData,
      });

      if (response && response?.status === 200) {
        if (response.data.nominationId) {
          setNominationId(response.data.nominationId);
        }

        const proposerUser = usersArray.find(
          (user) => user.fellowId === formattedData.proposerFellowId,
        );

        const proposerUserId = proposerUser?.id ?? formattedData.userId;
        const nominationDraftResponse = await axiosGet(
          `nomination-drafts?filters[userId][$eq]=${proposerUserId}&filters[name][$eq]=${formattedData.name}&filters[lastName][$eq]=${formattedData.lastName}`,
        );

        if (nominationDraftResponse.data.data.length !== 0) {
          const draftId = nominationDraftResponse.data.data[0].id;
          const nominationDraftHistory =
            nominationDraftResponse.data.data[0].attributes;

          const deleteResponse = await axiosDelete(
            `nomination-drafts/${draftId}`,
          );
          if (deleteResponse.status === 200) {
            setRefreshNominations(!refreshNominations);
            setNominationStatusId(1);

            const nominationDraftHistoryResponse = await axiosPost(
              'nomination-draft-histories',
              { data: nominationDraftHistory },
            );
            if (nominationDraftHistoryResponse.status === 200) {
              setRefreshNominations(!refreshNominations);
            }
          }
        } else {
          setNominationStatusId(1);
          setRefreshNominations(!refreshNominations);
        }
      }

      const responseData =
        Object.values(response.data).length !== 0
          ? response.data.candidateId
          : 0;

      setStrapiCandidateId(responseData);
    } catch (error) {
      devError('Error submitting nomination:', error);
      const serverMessage = error?.response?.data?.error?.message;
      const message =
        error?.response?.status === 409
          ? (serverMessage ??
            'This nomination has already been submitted. Reload the page to view the latest version.')
          : (serverMessage ?? 'Failed to submit nomination. Please try again.');
      showNotification('Form Errors', message);
    } finally {
      setSubmitPending(false);
    }
  };

  return {
    nominationStatusId,
    notification,
    showNotification,
    submitPending,
    pendingElectionChange,
    handleElectionTypeConfirm,
    handleElectionTypeCancel,
    onSubmit,
  };
};

export default useNominationWizard;
